// Kiosk / wall-screen token handoff (face-pass carryover, product glue):
// a ?st_token= in the URL is consumed ONCE into the session and stripped
// before the app mounts — the HMI posture (and what makes one-shot
// headless captures possible). Never logged, never left in the URL.
try {
  const u = new URL(window.location.href);
  const st = u.searchParams.get('st_token');
  if (st) {
    localStorage.setItem('token', st);
    // Kiosk admin surfaces (and the evidence captures): st_admin=1 arms
    // the admin-mode toggle for this session — the server still gates
    // every admin API by group, this only opens the CLIENT toggle.
    if (u.searchParams.get('st_admin') === '1') {
      sessionStorage.setItem(`${STORAGE_PREFIX}_adminMode`, 'true');
    }
    u.searchParams.delete('st_token');
    u.searchParams.delete('st_admin');
    window.history.replaceState(null, '', u.pathname + (u.search || '') + u.hash);
  }
} catch { /* URL parse failure — normal login flow */ }

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from '@app/App';
import { APP_TITLE, STORAGE_PREFIX } from '@client/config/client.config';
import '@app/index.css';
import { installPhoneViewport } from '@platform/app-shell/frontend/utils/phoneViewport';

// PWA service worker — registered manually (vite.config.js sets
// injectRegister: false) so WE decide when a new deployment may reload
// the page:
//   • A deploy replacing a stale precached shell right at page load
//     reloads within seconds, so the first visit after a deploy runs
//     the fresh bundle (the old behaviour served one stale load, which
//     stranded invite links on pre-deploy bundles).
//   • An established session is NEVER force-reloaded — after the grace
//     window the new worker takes over silently and applies on the
//     next load, exactly the pre-existing behaviour (no unsaved work
//     lost to a deploy).
//   • /auth/* landings never reload: invite/reset links carry one-time
//     tokens consumed at mount, and a reload there strands the user
//     (see also workbox.navigateFallbackDenylist for those paths, and
//     ResetPassword's session-recovery marker as the second seatbelt).
if ('serviceWorker' in navigator) {
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return;                                // first-ever install — nothing stale
    if (window.location.pathname.startsWith('/auth/')) return; // one-time-token pages
    if (performance.now() > 30000) return;                     // established session — apply next load
    window.location.reload();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* dev server has no sw.js */ });
  });
}

// A deploy renames every hashed chunk, and an established session is
// deliberately NOT force-reloaded (above) — so a tab can outlive the build it
// was served by. It survives happily until it lazy-loads a route, asks for a
// chunk that no longer exists, and dies with a blank screen: the route never
// mounts and React has nothing to render.
//
// Vite raises vite:preloadError for exactly this. One reload lands the tab on
// the current shell and the route loads normally. The marker makes it ONE
// reload: if the new shell fails the same way the failure is real, and it must
// surface as an error rather than a reload loop. Any successful load clears it.
const RELOAD_MARK = 'stride:chunk-reload';
window.addEventListener('vite:preloadError', (event) => {
  let tried = false;
  // Storage can throw outright (private mode, blocked site data). A tab that
  // cannot remember is still better off reloading once than sitting blank.
  try { tried = !!sessionStorage.getItem(RELOAD_MARK); } catch { /* unreadable */ }
  if (tried) return;                                // already tried — let it throw
  try { sessionStorage.setItem(RELOAD_MARK, String(Date.now())); } catch { /* unwritable */ }
  event.preventDefault();
  window.location.reload();
});
window.addEventListener('load', () => {
  try { sessionStorage.removeItem(RELOAD_MARK); } catch { /* private mode */ }
});

document.title = APP_TITLE;
console.log('main.jsx: React mounting...');

// Eruda — phone-friendly in-page console. Triggered by adding `?debug=1`
// to the URL. Once activated, it persists in sessionStorage so links
// internal to the app keep the debug overlay alive without re-adding
// the param. No-op for normal users — the script tag is only injected
// when explicitly requested.
(function maybeLoadEruda() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === '1') sessionStorage.setItem('zi-debug', '1');
    if (sessionStorage.getItem('zi-debug') !== '1') return;
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/eruda@3';
    s.onload = () => { try { window.eruda?.init(); console.log('[debug] Eruda console loaded'); } catch (e) { console.error('[debug] Eruda init failed', e); } };
    document.head.appendChild(s);
  } catch (e) { /* ignore — debug helper must never break the app */ }
})();

// ───── Phone viewport ─────
// The desktop layout pins panes to viewport edges (body height:100vh,
// tab-panes position:absolute inset:0), so a phone at width=device-width
// sees content cropped at the right. A touch device therefore gets the
// desktop pages at a fixed 1280px viewport, scaled to fit, UNLESS the page
// in front is phone-ready — then it gets its real device width. The rule,
// the list of ready routes and the one writer of the viewport tag live in
// phoneViewport.js.
installPhoneViewport();

// ───── iOS pull-to-refresh guard ─────
// On iOS, releasing one finger after a pinch-out leaves a single-finger
// downward swipe in flight. If that lands at the very top of the page,
// Safari's chrome-level handler interprets it as pull-to-refresh and
// reloads the page, even with overscroll-behavior:none and
// position:fixed body. The CSS gets us 90% of the way; this handler
// catches the residual case by preventDefault'ing the touchmove only
// when (a) we're a touch device, (b) it's a single-touch downward drag,
// and (c) the scrolling element is already at the top. Internal
// scrollable panels (which have their own scroll containers) keep
// working because their scrollTop > 0 once they've scrolled, and their
// touchmove targets aren't the document body.
(function setupPullRefreshGuard() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const isTouch = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  if (!isTouch) return;

  let touchStartY = null;
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      touchStartY = e.touches[0].clientY;
    } else {
      // Multi-touch (pinch) — let the browser handle it natively.
      touchStartY = null;
    }
  }, { passive: true });

  document.addEventListener('touchmove', (e) => {
    if (touchStartY === null || e.touches.length !== 1) return;
    const dy = e.touches[0].clientY - touchStartY;
    if (dy <= 0) return; // upward / sideways drag — leave alone
    // If the touch target sits inside a scrollable element that is itself
    // scrolled (or can scroll), let it through. We only block when there
    // is genuinely no scroll-up to consume the gesture, which is when
    // pull-to-refresh would otherwise trigger.
    const target = e.target;
    let el = target instanceof Element ? target : null;
    while (el) {
      if (el.scrollHeight > el.clientHeight && el.scrollTop > 0) return;
      el = el.parentElement;
    }
    e.preventDefault();
  }, { passive: false });
})();

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

console.log('main.jsx: React mounted');
